<template>
    <modal :title="modalTitle" @close_modal="close_modal" :loading="loading"
    >
        <template v-slot:title>
            {{ modalTitle }}
        </template>
        <template v-slot:content>
            <row>
                <col12>
                    <v-text-field
                            v-model="name"
                            required
                            dense
                    ></v-text-field>
                </col12>
            </row>
            <row>
                <col12>
                    <v-btn-toggle
                            id="annotate_type"
                            v-model="annotate_type"
                            mandatory
                            density="compact"
                            color="primary"
                            divided
                            class="mb-1"
                    >
                      <v-btn value="asset" size="small">
                        {{$t('settings_tasks.annotate_type_asset') || 'Asset'}}
                      </v-btn>
                      <v-btn value="crop" size="small" data-testid="annotate-type-crop">
                        {{$t('settings_tasks.annotate_type_crop') || 'Crop'}}
                      </v-btn>
                    </v-btn-toggle>
                    <div class="text-caption text-medium-emphasis">
                      {{$t('settings_tasks.annotate_type') || 'Annotate type'}}
                    </div>
                    <div v-if="annotate_type === 'crop'" class="text-caption text-medium-emphasis mt-1">
                      {{$t('settings_tasks.annotate_type_crop_hint') || 'Crop tasks validate bounding-box crops. Enable “Group by label” in the filters to use the group-by crops view.'}}
                    </div>
                </col12>
            </row>
            <row v-if="initiated">
                <col6>
                    <filter-tree
                            ref="filterTree"
                            :initial_filter="filter"
                            :max_results="max_results"
                            @update="updateFilterData"
                            @refresh="updateFilterData"
                            :show_button="false"
                            :show_limit="false"
                            @load="updateFilterData"></filter-tree>
                    <filter-save-load
                            v-if="!is_new_task"
                            :filters="filterArray"
                            :limit="limit"
                            page-name="edit_task"
                            @load="loadSavedFilter"
                    ></filter-save-load>
                </col6>
                <col6 v-if="users_loaded && initiated">
                    <user-list @select_users_ids="select_users_ids" :users="users" :selected_ids="task_user_ids"></user-list>
                </col6>
            </row>

        </template>
        <template v-slot:buttons>
            <v-btn
                    color="primary"
                    text
                    id="cancel_button"
                    @click="cancel"
            >
                {{$t('settings_tasks.edit_btn_cancel')}}
            </v-btn>

            <v-btn
                    color="primary"
                    text
                    id="save_button"
                    @click="save"
            >
                {{$t('settings_tasks.edit_btn_save')}}
                <v-icon
                        dark
                        primary
                        right
                >
                    fas fa-level-down-alt fa-rotate-90
                </v-icon>
            </v-btn>
        </template>
    </modal>
</template>

<script>
  import Modal from "@/components/Modal";
  import FilterTree from "@/components/asset_filter/FilterTree";
  import FilterSaveLoad from "@/components/FilterSaveLoad";
  import Row from "@/components/layout/Row";
  import Col6 from "@/components/layout/Col6";
  import Col12 from "@/components/layout/Col12";
  import LoadUsersMixin from "@/mixins/LoadUsersMixin";
  import UserList from "@/components/users/UserList";
  import { ensureGroupByLabelFilter, parseTaskFilters } from "@/utils/cropTask";
  export default {
    name: "EditTask",
    data () {
      return {
        'loading': false,
        'max_results': 1,
        'filter': '',
        'name': '',
        'annotate_type': 'asset',
        'initiated': false,
        'task_user_ids': [],
        'db_task_user_ids': [],
        'limit': 12
      }
    },
    props: {
      'task': {required: true},
      'is_new_task': {type: Boolean, default: false},
      'is_copy': {type: Boolean, default: false},
    },
    mixins: [LoadUsersMixin],
    computed: {
      modalTitle() {
        if (this.is_copy) {
          return this.$t('settings_tasks.copy_title');
        }
        if (this.is_new_task) {
          return this.$t('settings_tasks.add_title');
        }
        return this.$t('settings_tasks.edit_title');
      },
      filterArray() {
        // FilterSaveLoad accepts legacy arrays or {op, groups} objects.
        if (this.filter === '' || !this.filter) {
          return false;
        }
        return this.filter;
      }
    },
    mounted() {
      let filter = '';
      const raw = this.task.filter;
      if (raw != null && raw !== '') {
        try {
          filter = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (e) {
          console.warn('EditTask: could not parse task.filter', e);
          filter = '';
        }
      }
      this.filter = filter;
      this.name = this.task.name;
      this.annotate_type = (this.task.annotate_type === 'crop') ? 'crop' : 'asset';
      if (this.is_new_task) {
        this.initiated = true;
        return;
      }
      this.load_task_users();
    },
    watch: {
      annotate_type(value, oldValue) {
        if (value === oldValue) {
          return;
        }
        this.onAnnotateTypeChange(value);
      },
    },
    methods: {
      onAnnotateTypeChange(value) {
        this.annotate_type = value === 'crop' ? 'crop' : 'asset';
        if (this.annotate_type !== 'crop') {
          return;
        }
        // When switching to crop, include Group by label so the task view can use the crops modal UI.
        const next = ensureGroupByLabelFilter(parseTaskFilters(this.filter));
        this.filter = next;
        this.$nextTick(() => {
          if (this.$refs.filterTree && typeof this.$refs.filterTree.loadFilters === 'function') {
            this.$refs.filterTree.loadFilters(next, this.limit);
          }
        });
      },
      async load_task_users() {
        let users = await this.$api.user_task.all(this.task.id)
        const ids = (users || []).map((user) => Number(user.id))
        this['db_task_user_ids'] = ids
        this['task_user_ids'] = [...ids]
        this.initiated = true
      },
      updateFilterData (filter, limit) {
        this.filter = filter
        if (limit !== undefined) {
          this.limit = limit
        }
      },
      loadSavedFilter(filters, limit) {
        // Load saved filter into FilterTree
        if (this.$refs.filterTree) {
          this.$refs.filterTree.loadFilters(filters, limit);
        }
        // Update local state
        this.filter = filters;
        if (limit !== undefined) {
          this.limit = limit;
        }
      },
      cancel() {
        this.$emit('close_modal')
      },
      select_users_ids(user_ids) {
        this.task_user_ids = (user_ids || []).map((id) => Number(id));
      },
      close_modal() {
        this.$emit('close_modal')
      },
      filterPayload() {
        const tree = this.$refs.filterTree;
        if (tree && typeof tree.apiFilters === 'function') {
          const api = tree.apiFilters();
          const flat = Array.isArray(api) ? api : (api && api.groups ? api.groups.flat() : []);
          if (flat.length > 0 || (api && api.groups && api.groups.length > 1)) {
            return JSON.stringify(api);
          }
        }
        const f = this.filter;
        if (f === '' || f == null) {
          return '';
        }
        if (Array.isArray(f) && f.length === 0) {
          return '';
        }
        if (typeof f === 'string') {
          return f;
        }
        return JSON.stringify(f);
      },
      async save() {
        const filter = this.filterPayload();
        const name = this.name;
        const annotate_type = this.annotate_type === 'crop' ? 'crop' : 'asset';
        const normIds = (ids) =>
          [...new Set((ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];

        if (this.is_new_task) {
          const created = await this.$store.dispatch('task/add', { name, filter, annotate_type });
          if (!created || !created.id) {
            return;
          }
          const selectedIds = normIds(this.task_user_ids);
          for (let i = 0; i < selectedIds.length; i++) {
            await this.$api.user_task.add(created.id, selectedIds[i]);
          }
          this.close_modal();
          return;
        }

        const taskId = this.task.id;
        const dbIds = normIds(this.db_task_user_ids);
        const selectedIds = normIds(this.task_user_ids);

        for (let i = 0; i < dbIds.length; i++) {
          const user_id = dbIds[i];
          if (selectedIds.indexOf(user_id) === -1) {
            await this.$api.user_task.delete(taskId, user_id);
          }
        }
        for (let i = 0; i < selectedIds.length; i++) {
          const user_id = selectedIds[i];
          if (dbIds.indexOf(user_id) === -1) {
            await this.$api.user_task.add(taskId, user_id);
          }
        }

        const item = { id: taskId, name, filter, annotate_type };
        await this.$store.dispatch('task/patch', {
          item,
          dispatch: this.$store.dispatch,
        });

        this.close_modal();
      }
    },
    components: {UserList, Col12, Col6, Row, FilterTree, FilterSaveLoad, Modal}
  }
</script>

<style scoped>

</style>